import React, { useState } from 'react';
import type { ReactNode } from 'react';

import { Button } from '../../ui/Button/Button';

import { IconPencil, PencilIcon, TrashBinIcon } from '../../../assets/icons/icons';
import type { SizeSort, SizeSortKey, TypicalSize } from '../../../types/sizes';
import { DimensionValue } from '../DimensionValue/DimensionValue';
import { WeightValue } from '../WeightValue/WeightValue';
import { TransportNumber } from '../TransportNumber/TransportNumber';
import styles from './SizesTable.module.scss';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import { DataTable, tableStyles } from '../../ui/DataTable/DataTable';
import { SortableTh } from '../../ui/DataTable/SortableTh';
import { CatalogThumb } from '../../ui/CatalogThumb/CatalogThumb';
import { cx } from '../../../utils/cx';

interface SizesTableProps {
    sizes: TypicalSize[];
    sort: SizeSort;
    startIndex?: number;
    footer?: ReactNode;
    empty?: ReactNode;
    isLoading?: boolean;
    loading?: boolean;
    loadingText?: string;
    scrollKey?: string | number;
    onSort: (key: SizeSortKey) => void;
    onSave: (id: string, values: Record<string, unknown>) => void;
    onDelete: (size: TypicalSize) => void;
}

const NUMERIC_COLUMNS: { key: SizeSortKey; label: string }[] = [
    { key: 'length', label: 'Длина, мм' },
    { key: 'width', label: 'Ширина, мм' },
    { key: 'height', label: 'Высота, мм' },
];

function hostOf(source: string | null): string | null {
    if (!source) return null;

    try {
        return new URL(source).hostname.replace(/^www\./, '');
    } catch {
        return source;
    }
}

const NUM_FIELDS = ['length', 'width', 'height', 'weight'] as const;
type NumField = (typeof NUM_FIELDS)[number];

const TRANSPORT_KEY: Record<NumField, keyof TypicalSize> = {
    length: 'transportLength',
    width: 'transportWidth',
    height: 'transportHeight',
    weight: 'transportWeight',
};

const EMPTY_DRAFT: Record<NumField, string> = {
    length: '',
    width: '',
    height: '',
    weight: '',
};

const AUTO_AUTHORS = new Set(['consensus', 'ai']);

export function SizesTable({
    sizes,
    sort,
    startIndex = 0,
    footer,
    empty,
    isLoading,
    loading,
    loadingText,
    scrollKey,
    onSort,
    onSave,
    onDelete,
}: SizesTableProps) {
    const [editingId, setEditingId] = useState<string | null>(null);

    const [draft, setDraft] = useState<Record<NumField, string>>(EMPTY_DRAFT);
    const [tDraft, setTDraft] = useState<Record<NumField, string>>(EMPTY_DRAFT);

    const authorOf = (author: string | null): string => {
        if (!author) return '—';
        return AUTO_AUTHORS.has(author.toLowerCase()) ? 'ИИ-агент' : author;
    };

    const startEdit = (size: TypicalSize) => {
        setDraft({
            length: String(size.length ?? ''),
            width: String(size.width ?? ''),
            height: String(size.height ?? ''),
            weight: String(size.weight ?? ''),
        });
        setTDraft({
            length: String(size.transportLength ?? ''),
            width: String(size.transportWidth ?? ''),
            height: String(size.transportHeight ?? ''),
            weight: String(size.transportWeight ?? ''),
        });
        setEditingId(size.id);
    };

    const isBaseChanged = (size: TypicalSize) =>
        NUM_FIELDS.some((field) => (Number(draft[field]) || null) !== (size[field] ?? null));

    const isTransportChanged = (size: TypicalSize) =>
        NUM_FIELDS.some(
            (field) =>
                (Number(tDraft[field]) || null) !==
                ((size[TRANSPORT_KEY[field]] as number | null) ?? null),
        );

    const isChanged = (size: TypicalSize) => isBaseChanged(size) || isTransportChanged(size);

    const save = (size: TypicalSize) => {
        const num = (raw: string) => Number(raw) || null;

        const body: Record<string, unknown> = {};

        if (isBaseChanged(size)) {
            body.length = num(draft.length);
            body.width = num(draft.width);
            body.height = num(draft.height);
            body.maximum_load_weight = num(draft.weight);
        }

        if (isTransportChanged(size)) {
            body.transport_length = num(tDraft.length);
            body.transport_width = num(tDraft.width);
            body.transport_height = num(tDraft.height);
            body.transport_weight = num(tDraft.weight);
        }

        onSave(size.id, body);
        setEditingId(null);
    };

    const labelCell = () => (
        <td className={`${styles.number} ${styles.labels}`}>
            <span className={styles.stack}>
                <span className={styles.main}>
                    <i>эталон</i>
                </span>
                <span className={styles.transport}>
                    <i>трансп.</i>
                </span>
            </span>
        </td>
    );

    const numCell = (size: TypicalSize, field: NumField) => (
        <td className={`${tableStyles.num} ${styles.number}`}>
            {editingId === size.id ? (
                <span className={styles.stack}>
                    <span className={styles.main}>
                        <input
                            className={styles.input}
                            inputMode="numeric"
                            aria-label={`${field}, эталон`}
                            value={draft[field]}
                            onChange={(event) =>
                                setDraft((prev) => ({
                                    ...prev,
                                    [field]: event.target.value.replace(/\D/g, ''),
                                }))
                            }
                        />
                    </span>

                    <span className={styles.transport}>
                        <input
                            className={styles.input}
                            inputMode="numeric"
                            aria-label={`${field}, транспортный`}
                            value={tDraft[field]}
                            onChange={(event) =>
                                setTDraft((prev) => ({
                                    ...prev,
                                    [field]: event.target.value.replace(/\D/g, ''),
                                }))
                            }
                        />
                    </span>
                </span>
            ) : (
                <span className={styles.stack}>
                    <span className={styles.main}>
                        {field === 'weight' ? (
                            <WeightValue grams={size.weight} />
                        ) : (
                            <DimensionValue value={size[field]} />
                        )}
                    </span>

                    <span
                        className={styles.transport}
                        title={
                            size.transportIsManual
                                ? 'Транспортный габарит, задан вручную'
                                : 'Транспортный габарит'
                        }
                    >
                        <TransportNumber value={size[TRANSPORT_KEY[field]] as number | null} />
                    </span>
                </span>
            )}
        </td>
    );

    return (
        <DataTable
            static
            colgroup={
                <colgroup>
                    <col style={{ width: 36 }} />
                    <col style={{ width: '36%' }} />
                    <col style={{ width: 64 }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '24%' }} />
                    <col style={{ width: 190 }} />
                </colgroup>
            }
            footer={footer}
            empty={empty}
            isLoading={isLoading}
            loading={loading}
            loadingText={loadingText}
            scrollKey={scrollKey}
        >
            <thead>
                <tr>
                    <th className={tableStyles.index}>№</th>
                    <SortableTh
                        title="Наименование"
                        active={sort.key === 'name'}
                        dir={sort.direction}
                        onClick={() => onSort('name')}
                    />

                    {/* эталон трансп. */}
                    <th />

                    {NUMERIC_COLUMNS.map((column) => (
                        <SortableTh
                            key={column.key}
                            title={column.label}
                            active={sort.key === column.key}
                            dir={sort.direction}
                            align="right"
                            onClick={() => onSort(column.key)}
                        />
                    ))}

                    <SortableTh
                        title="Вес, г"
                        active={sort.key === 'weight'}
                        dir={sort.direction}
                        align="right"
                        onClick={() => onSort('weight')}
                    />

                    <th>Кто добавил</th>

                    <th className={cx(tableStyles.actions, styles.actionsCol)} aria-label="Действия" />
                </tr>
            </thead>

            <tbody>
                {sizes.map((size, index) => {
                    const host = hostOf(size.source);

                    return (
                        <tr key={size.id}>
                            <td className={tableStyles.index}>{startIndex + index + 1}</td>
                            <td className={styles.item}>
                                <div className={styles.itemInner}>
                                    <CatalogThumb src={size.imageUrl} />

                                    <div className={styles.itemText}>
                                        <div className={styles.name}>{size.name}</div>

                                        {size.sourceTitles.length > 0 && (
                                            <Tooltip text={size.sourceTitles.join(',\n')} align="left">
                                                <div className={styles.category}>{size.sourceTitles[0]}</div>
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                            </td>

                            {labelCell()}
                            {numCell(size, 'length')}
                            {numCell(size, 'width')}
                            {numCell(size, 'height')}
                            {numCell(size, 'weight')}

                            <td className={tableStyles.muted}>{authorOf(size.author)}</td>

                            <td className={tableStyles.actions}>
                                {editingId === size.id ? (
                                    <span className={styles.editActions}>
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={!isChanged(size)}
                                            onClick={() => save(size)}
                                        >
                                            Сохранить
                                        </Button>
                                        <Button size="sm" onClick={() => setEditingId(null)}>
                                            Отмена
                                        </Button>
                                    </span>
                                ) : (
                                    <span className={styles.rowActions}>
                                        <Tooltip text="Изменить габариты" align="right">
                                            <button
                                                type="button"
                                                className={styles.iconBtn}
                                                aria-label="Изменить габариты"
                                                onClick={() => startEdit(size)}
                                            >
                                                <PencilIcon />
                                            </button>
                                        </Tooltip>

                                        <Tooltip text="Удалить из справочника" align="right">
                                            <button
                                                type="button"
                                                className={`${styles.iconBtn} ${styles.danger}`}
                                                aria-label="Удалить из справочника"
                                                onClick={() => onDelete(size)}
                                            >
                                                <TrashBinIcon />
                                            </button>
                                        </Tooltip>
                                    </span>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </DataTable>
    );
}